import { Download } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PaymentAdjustmentModal } from '@/features/application/components/modals/PaymentAdjustmentModal'
import type { PaymentAdjustmentHistoryLine } from '@/features/application/types/modals'
import { submitPaymentRefundAdjustment } from '@/features/application/services/applicationService'
import { generateChargebackPacket } from '@/features/reports/services/reportsService'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { BalanceReportFilters } from '../components/balance-report/BalanceReportFilters'
import { BalanceReportTable } from '../components/balance-report/BalanceReportTable'
import { SendInvoiceModal } from '../components/balance-report/SendInvoiceModal'
import { useBalanceReportPage } from '../hooks/useBalanceReportPage'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'

export function BalanceReportPage() {
  const page = useBalanceReportPage()

  return (
    <div data-testid="balance-report-page">
      <PageShell
        title="Balance Report (Outstanding/Adjustment)"
        pageHeaderTestIdRoot="balance-report-page-header"
        contentClassName="!space-y-5 w-full max-w-full min-w-0 !px-2 sm:!px-4 lg:!px-5"
      >
        <ListSearchToolbar
          searchValue={page.searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={page.setSearchDraft}
          activeFilterCount={page.activeFilterCount}
          onOpenFilters={() => page.setFiltersOpen(true)}
          testIdPrefix="balance-report"
          actions={
            page.exportHref ? (
              <a
                href={page.exportHref}
                target="_blank"
                rel="noopener noreferrer"
                className={REPORT_EXPORT_BUTTON_CLASS}
              >
                <Download className="h-[18px] w-[18px]" aria-hidden />
                Export
              </a>
            ) : undefined
          }
        />

        <BalanceReportFilters
          filters={page.params}
          isOpen={page.filtersOpen}
          onClose={() => page.setFiltersOpen(false)}
          onApply={(next) => page.setParams({ ...next, page: 1 })}
          onReset={page.resetFilters}
        />

        <BalanceReportTable
          loading={page.loading}
          params={page.params}
          rows={page.rows}
          pageOptions={page.pageOptions}
          expandedRows={page.expandedRows}
          canOpenApplication={page.canOpenApplication}
          canPerformPaymentActions={page.canPerformPaymentActions}
          gatewayMeta={page.gatewayMeta}
          adjustmentLoadingKey={page.adjustmentLoadingKey}
          onSort={page.sort}
          onPageChange={(pageNum) => page.setParams((p) => ({ ...p, page: pageNum }))}
          onPerPageChange={(perPage) =>
            page.setParams((p) => ({ ...p, perPage, page: 1 }))
          }
          onToggleExpanded={page.toggleExpanded}
          onSendInvoice={page.setInvoiceRow}
          onOpenPaymentAdjustment={page.openPaymentAdjustment}
          canEditPayment={page.canEditPayment}
          canDeletePayment={page.canDeletePayment}
          deletePaymentLoadingKey={page.deletePaymentLoadingKey}
          onDeletePayment={page.requestDeletePayment}
        />
      </PageShell>

      {page.adjustmentModal.payment && page.adjustmentModal.application ? (
        <PaymentAdjustmentModal
          isOpen
          onClose={page.closeAdjustmentModal}
          payment={page.adjustmentModal.payment as PaymentAdjustmentHistoryLine}
          application={page.adjustmentModal.application}
          requireNote={false}
          onSubmit={async (data) => {
            try {
              if (!page.adjustmentModal.payment?.payment_log_id) {
                showApiError({
                  message: 'Cannot adjust payment without a payment log id.',
                })
                return
              }
              await submitPaymentRefundAdjustment(
                page.adjustmentModal.payment.payment_log_id,
                data,
              )
              showSuccess('Payment Adjustment Added Successfully!')
              page.closeAdjustmentModal()
              await page.load(page.params)
            } catch (err) {
              showApiError(err)
              throw err
            }
          }}
          onGenerateChargebackPacket={async ({ applicantId, paymentLogId }) => {
            try {
              await generateChargebackPacket(applicantId, paymentLogId)
              showSuccess('Chargeback Packet Generated Sucessfully!')
              page.closeAdjustmentModal()
              await page.load(page.params)
            } catch (err) {
              showApiError(err)
            }
          }}
        />
      ) : null}

      {page.invoiceRow ? (
        <SendInvoiceModal
          row={page.invoiceRow}
          sending={page.invoiceSending}
          onClose={() => page.setInvoiceRow(null)}
          onSubmit={page.submitInvoiceReminder}
        />
      ) : null}

      <ConfirmDialog
        isOpen={page.deleteConfirm != null}
        title="Delete Payment Adjustment"
        message="Do you want to Delete this Payment Adjustment?"
        primaryLabel="Delete"
        secondaryLabel="Cancel"
        onPrimary={() => void page.confirmDeletePayment()}
        onSecondary={page.cancelDeletePayment}
        onDismiss={page.cancelDeletePayment}
      />
    </div>
  )
}
